import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Container,
  Radio,
  useTheme,
  CircularProgress,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import API from "../../../../api/Axios";
import certificateReport2025 from "../../../../assets/images/icons/certificate-report-2025.svg";
import certificateReport20xx from "../../../../assets/images/icons/certificate-report-20xx.svg";
import noDataFound from "../../../../assets/images/icons/no-data-found.svg";
import bannerImg8Bg from "../../../../assets/images/icons/bannerImg8-bg.jpg";
import "./DownloadCertificate.scss";

interface AcademicYearItem {
  year: string;
  academic_year: string;
  eligibility: boolean;
  not_eligibile_reason?: string;
  not_eligible_reason?: string;
  rating_certificate_hide?: boolean;
}

const DownloadCertificate: React.FC = () => {
  const [academicYears, setAcademicYears] = useState<AcademicYearItem[]>([]);
  const [selectedYear, setSelectedYear] = useState<string | null>(null);
  const [activeReasonYear, setActiveReasonYear] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation("home");
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const isDesktop = useMediaQuery("(hover: hover) and (pointer: fine)");

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(".download-year-card.disabled")) {
        setActiveReasonYear(null);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchAcademicYears = async () => {
      try {
        setLoading(true);
        const response = await API.get("/certificate/academic-years-list");
        const data = response?.data?.data;

        // If top-level rating_certificate_hide is true, hide all
        if (
          data?.rating_certificate_hide === true ||
          data?.rating_certificate_hide === "true"
        ) {
          setAcademicYears([]);
          setSelectedYear(null);
          return;
        }

        const rawYears: any[] = data?.academic_years || [];

        // Normalize data and filter out items where rating_certificate_hide is true
        const normalized: AcademicYearItem[] = rawYears
          .filter((item) => {
            if (typeof item === "object" && item !== null) {
              return (
                item.rating_certificate_hide !== true &&
                item.rating_certificate_hide !== "true"
              );
            }
            return true;
          })
          .map((item) => {
            if (typeof item === "object" && item !== null) {
              return {
                year: String(item.year || ""),
                academic_year: item.academic_year || String(item.year || ""),
                eligibility: item.eligibility !== false,
                not_eligibile_reason:
                  item.not_eligibile_reason || item.not_eligible_reason || "",
                rating_certificate_hide: Boolean(item.rating_certificate_hide),
              };
            }
            const yStr = String(item);
            const yNum = Number(item);
            const academicYear = !isNaN(yNum)
              ? `${yNum}-${((yNum + 1) % 100).toString().padStart(2, "0")}`
              : yStr;
            return {
              year: yStr,
              academic_year: academicYear,
              eligibility: true,
              not_eligibile_reason: "",
            };
          });

        setAcademicYears(normalized);

        // Auto-select first eligible year, or first item if none eligible
        const firstEligible = normalized.find((item) => item.eligibility);
        if (firstEligible) {
          setSelectedYear(firstEligible.year);
        } else if (normalized.length > 0) {
          setSelectedYear(normalized[0].year);
        }
      } catch (err) {
        console.error("[Academic Years List] error", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAcademicYears();
  }, []);

  const selectedYearItem = academicYears.find(
    (item) => item.year === selectedYear
  );
  const isProceedDisabled =
    !selectedYear || !selectedYearItem || !selectedYearItem.eligibility;

  const handleProceed = () => {
    if (isProceedDisabled || !selectedYear) return;
    const targetPath = location.pathname.startsWith("/school-dashboard")
      ? "/school-dashboard/certificates/view"
      : "/certificates/view";
    navigate(targetPath, { state: { year: selectedYear } });
  };

  return (
    <Box
      className="download-certificate-page"
      sx={{
        pt: { xs: 2, md: 3.5 },
        minHeight: { xs: "calc(100vh - 132px)", md: "calc(100vh - 150px)" },
        width: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "transparent",
      }}
    >
      <Container
        maxWidth={false}
        className="download-certificate-container"
        sx={{
          position: "relative",
          overflow: "hidden",
          width: { xs: "calc(100% - 32px)", sm: "94%", md: "88%" },
          minHeight: { xs: "calc(100vh - 260px)", md: "calc(100vh - 330px)" },
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          px: { xs: "16px !important", sm: "28px !important", md: "46px !important" },
          py: { xs: 3, md: 4.5 },
          border: `1px solid ${isDarkMode ? "#5f6666" : "#5de4d2"}`,
          borderBottom: 0,
          borderRadius: { xs: "24px 24px 0 0", md: "44px 44px 0 0" },
          backgroundColor: isDarkMode ? "#0d1915" : "#ffffff",
          backgroundImage: isDarkMode
            ? `linear-gradient(rgba(5, 15, 12, 0.60), rgba(5, 15, 12, 0.90)), url(${bannerImg8Bg})`
            : `linear-gradient(rgba(255, 255, 255, 0.30), rgba(240, 255, 248, 0.88)), url(${bannerImg8Bg})`,
          backgroundSize: "cover",
          backgroundPosition: "center top",
          backgroundRepeat: "no-repeat",
          boxShadow: isDarkMode
            ? "0 14px 38px rgba(0, 0, 0, 0.4)"
            : "0 12px 32px rgba(25, 89, 66, 0.08)",
        }}
      >
        <Box className="download-certificate-heading">
            <Typography component="h1">
              {t("downloadCertificate.title", "Download Certificate")}
            </Typography>
            <Typography component="p">
              {t(
                "downloadCertificate.subtitle",
                "Please select a certificate year below."
              )}
            </Typography>
          </Box>

          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 6,
              }}
            >
              <CircularProgress sx={{ color: "#2e3b80" }} />
            </Box>
          ) : academicYears.length === 0 ? (
            <Box
              sx={{
                py: 4,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                textAlign: "center",
              }}
            >
              <img
                src={noDataFound}
                alt="No data found"
                style={{ width: "160px", maxWidth: "80%", height: "auto" }}
              />
              <Typography color="text.secondary">
                {t(
                  "downloadCertificate.noYearsAvailable",
                  "No academic years available."
                )}
              </Typography>
            </Box>
          ) : (
            <Box className="download-certificate-cards-wrapper">
              {academicYears.map((item) => {
                const isSelected = selectedYear === item.year;
                const isEligible = item.eligibility;
                const tooltipReason =
                  item.not_eligibile_reason || item.not_eligible_reason || "";
                const icon =
                  item.year === "2025"
                    ? certificateReport2025
                    : certificateReport20xx;

                const isOverlayOpen = !isDesktop && activeReasonYear === item.year;

                const cardElement = (
                  <button
                    key={item.year}
                    type="button"
                    className={`download-year-card${
                      isSelected ? " selected" : ""
                    }${!isEligible ? " disabled" : ""}`}
                    onClick={() => {
                      setSelectedYear(item.year);
                      if (isEligible) {
                        setActiveReasonYear(null);
                      } else if (!isDesktop && tooltipReason) {
                        // On mobile/tablet: toggle inside-card reason overlay on tap
                        setActiveReasonYear((prev) =>
                          prev === item.year ? null : item.year
                        );
                      }
                    }}
                    aria-pressed={isSelected}
                    aria-disabled={!isEligible}
                  >
                    <Radio
                      checked={isSelected}
                      disabled={!isEligible}
                      className="download-year-radio"
                      tabIndex={-1}
                    />

                    {/* Inside-card overlay: on mobile & tablets only */}
                    {!isEligible && isOverlayOpen && tooltipReason && (
                      <div className="download-card-reason-overlay">
                        <p className="reason-text">{tooltipReason}</p>
                        <span className="reason-hint">Tap to dismiss</span>
                      </div>
                    )}

                    <img
                      src={icon}
                      alt={`${item.academic_year} report`}
                      className="download-report-img"
                    />
                    <span className="download-year">{item.academic_year}</span>
                    <span className="download-label">
                      {t("downloadCertificate.reports")}
                    </span>
                  </button>
                );

                // On desktop: show MUI Tooltip on hover
                if (isDesktop && !isEligible && tooltipReason) {
                  return (
                    <Tooltip
                      key={item.year}
                      title={tooltipReason}
                      arrow
                      placement="top"
                    >
                      <span
                        style={{
                          display: "inline-flex",
                          flex: "0 1 300px",
                          width: "100%",
                          maxWidth: "300px",
                        }}
                      >
                        {cardElement}
                      </span>
                    </Tooltip>
                  );
                }

                return cardElement;
              })}
            </Box>
          )}

          {!loading && academicYears.length > 0 && (
            <>
              <Button
                variant="contained"
                className="download-proceed-button"
                onClick={handleProceed}
                disabled={isProceedDisabled}
                sx={{
                  "&.MuiButton-root:disabled": {
                    backgroundColor: "#cccccc !important",
                    color: "#666666 !important",
                    opacity: 1,
                  },
                }}
              >
                {t("downloadCertificate.proceed", "Proceed")}
              </Button>

              <Box
                sx={{
                  border: `1px solid ${isDarkMode ? "#59615d" : "#c7e8cc"}`,
                  borderRadius: "16px",
                  backgroundColor: isDarkMode ? "#252a2d" : "#fff",
                  px: { xs: 2.5, sm: 4 },
                  py: { xs: 1.5, md: 1.75 },
                  mt: 3.5,
                  maxWidth: "960px",
                  width: { xs: "100%", sm: "auto" },
                  textAlign: "center",
                  boxSizing: "border-box",
                }}
              >
                <Typography
                  sx={{
                    color: isDarkMode ? "#d0d0d0" : "#0F202B",
                    fontSize: "calc(14px * var(--dynamic-font-size, 1))",
                    fontWeight: 700,
                    lineHeight: 1.5,
                  }}
                >
                  {t(
                    "downloadCertificate.note"
                  )}
                </Typography>
              </Box>
            </>
          )}

          <Box sx={{ mt: "auto", height: { xs: 24, md: 40 }, flexShrink: 0 }} />

          <Box
            component="footer"
            sx={{
              alignSelf: "stretch",
              mx: { xs: -2, sm: -3.5, md: -5.75 },
              mb: { xs: -3, md: -4.5 },
              py: { xs: 2.5, md: 3 },
              textAlign: "center",
              borderTop: `1px solid ${isDarkMode ? "#50575a" : "#d7e2dc"}`,
            }}
          >
            <Typography
              sx={{
                color: isDarkMode ? "#b9b9b9" : "#777",
                fontSize: { xs: "12px", md: "14px" },
              }}
            >
              {t("footer.allRights", { year: new Date().getFullYear() })}
            </Typography>
          </Box>
      </Container>
    </Box>
  );
};

export default DownloadCertificate;
